import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  Mail,
  PenLine,
  Search,
  RefreshCw,
  Settings,
  Inbox,
  Star,
  Send,
  FileText,
  Trash2,
  AlertTriangle,
  Folder,
  Plus,
  Paperclip,
  Reply,
  ReplyAll,
  Forward,
  Archive,
  ChevronDown,
  RotateCcw,
  FolderInput,
  Save,
  Pencil,
  Check,
  Download,
  X,
  Link2,
  Image as ImageIcon,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  IndentIncrease,
  IndentDecrease,
  Table as TableIcon,
  RemoveFormatting,
  Code,
  ArrowLeft,
  Minus,
  Maximize2,
  Minimize2,
} from "lucide-react";

import "./EmailPage.css";
import { api } from "../api"; // project emails for the "To" / "Cc" dropdown
import MailAccounts from "./MailAccounts"; // SDC-MULTIMAIL
import MailThread from "./MailThread";
import MailHistory from "./MailHistory";

const isLocalhost = ["localhost", "127.0.0.1"].includes(
  window.location.hostname,
);

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  (isLocalhost ? "http://localhost:8080" : "https://erp-api.sdcllp.co.in")
).replace(/\/$/, "");

const API = `${API_BASE_URL}/api/email`;

// SDC-MULTIMAIL: extra mailbox selected right now ("" = main mailbox).
// Every email API call carries it as ?account=...
let currentAccount = "";

const withAccount = (path) =>
  currentAccount
    ? `${path}${path.includes("?") ? "&" : "?"}account=${encodeURIComponent(
        currentAccount,
      )}`
    : path;

// color / fill = icon colours (same as reference screenshot)
const SYSTEM_FOLDERS = [
  { id: "inbox", name: "Inbox", icon: Inbox, color: "#2f55c9", fill: "none" },
  {
    id: "starred",
    name: "Starred",
    icon: Star,
    color: "#f59e0b",
    fill: "#f59e0b",
  },
  { id: "sent", name: "Sent", icon: Send, color: "#3b5b8c", fill: "#3b5b8c" },
  {
    id: "drafts",
    name: "Drafts",
    icon: FileText,
    color: "#4b5563",
    fill: "none",
  },
  {
    id: "archive",
    name: "Archive",
    icon: Archive,
    color: "#6b4fbb",
    fill: "none",
  },
  { id: "trash", name: "Trash", icon: Trash2, color: "#4b5563", fill: "none" },
  {
    id: "spam",
    name: "Spam",
    icon: AlertTriangle,
    color: "#e5372b",
    fill: "none",
  },
];

const SYSTEM_IDS = new Set(SYSTEM_FOLDERS.map((folder) => folder.id));

// custom folder colours (teal, pink, navy, yellow, navy ...)
const FOLDER_COLORS = [
  "#2fa4a0",
  "#d6307f",
  "#1f2a5a",
  "#f0a500",
  "#6d3fc4",
  "#e8590c",
];

const AVATAR_COLORS = [
  "#2e8b3d",
  "#8b3fd9",
  "#d6306a",
  "#2f9d5a",
  "#e8590c",
  "#6a3fb5",
  "#1f9d8f",
  "#d6246e",
];

const colorFor = (text = "") =>
  AVATAR_COLORS[
    [...text].reduce((sum, char) => sum + char.charCodeAt(0), 0) %
      AVATAR_COLORS.length
  ];

const emptyCompose = {
  to: "",
  cc: "",
  bcc: "",
  subject: "",
  body: "",
  html: "",
  // reply: the original mail, shown collapsed behind "..." (like Gmail)
  quoteHtml: "",
  quoteText: "",
};

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  sessionStorage.getItem("token");

const asText = (value) => {
  if (value == null) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(asText).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return asText(
      value.name || value.address || value.text || value.value || "",
    );
  }

  return "";
};

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? asText(value)
    : date.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
};

// ---------- attachment helpers ----------

const fileKind = (name = "", type = "") => {
  const ext = String(name).split(".").pop().toLowerCase();

  if (type === "application/pdf" || ext === "pdf")
    return { cls: "pdf", label: "PDF" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { cls: "xls", label: "XLS" };
  if (["doc", "docx"].includes(ext)) return { cls: "doc", label: "DOC" };
  if (["ppt", "pptx"].includes(ext)) return { cls: "ppt", label: "PPT" };
  if (String(type).startsWith("image/")) return { cls: "img", label: "IMG" };
  if (["zip", "rar", "7z"].includes(ext)) return { cls: "zip", label: "ZIP" };

  return { cls: "", label: ext.slice(0, 4).toUpperCase() || "FILE" };
};

// only these can be previewed in a browser tab; others download on click
const isPreviewable = (type = "") =>
  type === "application/pdf" ||
  String(type).startsWith("image/") ||
  String(type).startsWith("text/");

const formatSize = (bytes) => {
  const n = Number(bytes);

  if (!Number.isFinite(n)) return "Size unavailable";

  return n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;
};

const normalizeMessage = (message, fallbackFolder) => {
  const folder = asText(message.folder || fallbackFolder);
  const uid = String(message.uid ?? message.id);

  const sender = asText(message.sender || message.from) || "Unknown sender";

  const initials =
    sender
      .replace(/<[^>]*>/g, "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0] || "")
      .join("")
      .toUpperCase() || "?";

  return {
    ...message,
    id: uid,
    uid,
    key: `${folder}:${uid}`,
    folder,
    sender,
    email: asText(message.to || message.from || ""),
    subject: asText(message.subject) || "(No subject)",
    preview: asText(message.preview || message.snippet || ""),
    date: formatDate(message.date || message.internalDate),
    initials,
    color: colorFor(sender),
    unread: Boolean(message.unread),
    starred: Boolean(message.starred),
    hasAttachments: Boolean(message.hasAttachments),
  };
};

const addressList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap(addressList);
  }

  if (typeof value === "object") {
    if (Array.isArray(value.value)) {
      return addressList(value.value);
    }

    return value.address ? [String(value.address)] : [];
  }

  return String(value)
    .split(/[;,]/)
    .map((part) => {
      const match = part.match(/<([^<>]+)>/);
      return (match ? match[1] : part).trim();
    })
    .filter((item) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item));
};

const uniqueAddresses = (...groups) => [
  ...new Set(groups.flatMap(addressList).map((email) => email.toLowerCase())),
];

const subjectWithPrefix = (subject, prefix) => {
  const clean = asText(subject).trim();

  return new RegExp(`^${prefix}:`, "i").test(clean)
    ? clean
    : `${prefix}: ${clean}`;
};

// subject without Re: / Fwd: - mails with the same one are one conversation
const threadSubject = (subject) =>
  asText(subject)
    .replace(/^\s*((re|fwd?|fw)\s*:\s*)+/i, "")
    .trim()
    .toLowerCase();

const apiRequest = async (
  path,
  { method = "GET", body, signal, raw = false } = {},
) => {
  const authToken = getToken();

  if (!authToken) {
    throw new Error("ERP login token not found. Please log in again.");
  }

  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  if (body !== undefined && !(body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  // SDC-MULTIMAIL: withAccount() adds ?account=... for an extra mailbox
  const response = await fetch(`${API}${withAccount(path)}`, {
    method,
    headers,
    signal,
    body:
      body instanceof FormData
        ? body
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
  });

  if (raw && response.ok) return response;

  const result = await response.json().catch(() => ({}));

  if (!response.ok || result.success === false) {
    throw new Error(
      response.status === 401
        ? "Your ERP session has expired. Please log in again."
        : result.message || `Email request failed (${response.status}).`,
    );
  }

  return result;
};

const folderQuery = (folder) => `?folder=${encodeURIComponent(folder)}`;

// ====================================================
// RICH TEXT HELPERS + EDITOR (no extra library needed)
// ====================================================

const escapeHtml = (text = "") =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const textToHtml = (text = "") => escapeHtml(text).replace(/\r?\n/g, "<br>");

const hasDraftContent = (c) =>
  Boolean(c.to.trim() || c.subject.trim() || c.body.trim());

// what is really sent / saved: typed text + quoted original (reply)
const fullText = (c) => c.body + (c.quoteText ? `\n\n${c.quoteText}` : "");

const fullHtml = (c) => (c.html || textToHtml(c.body)) + (c.quoteHtml || "");

const buildDraftBody = (c) => ({
  to: c.to.trim(),
  cc: c.cc.trim(),
  bcc: (c.bcc || "").trim(),
  subject: c.subject.trim(),
  text: fullText(c),
  html: fullHtml(c),
});

// Gmail style:  "On <date>, <name> wrote:"  + indented original
const replyQuote = (who, when, bodyText) => {
  const intro = `On ${when}, ${who} wrote:`;
  const body = String(bodyText || "");

  return {
    quoteHtml:
      `<br><div><div>${escapeHtml(intro)}</div>` +
      `<blockquote style="margin:0 0 0 0.8ex;border-left:1px solid #ccc;padding-left:1ex">` +
      `${textToHtml(body)}</blockquote></div>`,
    quoteText:
      `${intro}\n` +
      body
        .split(/\r?\n/)
        .map((line) => `> ${line}`)
        .join("\n"),
  };
};

const htmlToPlain = (html) => {
  const withBreaks = String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n");

  // DOMParser never runs scripts
  const doc = new DOMParser().parseFromString(withBreaks, "text/html");

  return (doc.body.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const isBlankHtml = (html) =>
  !/<img/i.test(html || "") &&
  !String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();

// signature block that goes into the editor of a new mail / reply / forward
const signatureBlock = (sig) =>
  sig?.enabled && !isBlankHtml(sig.html)
    ? `<br><br><div>-- <br>${sig.html}</div>`
    : "";

// Gmail style forward header (editable, goes inside the editor)
const forwardParts = (detail) => {
  const rows = [
    ["From", asText(detail.from)],
    ["Date", formatDate(detail.date)],
    ["Subject", asText(detail.subject)],
    ["To", asText(detail.to)],
  ];

  const body = asText(detail.body);

  return {
    body:
      "\n\n---------- Forwarded message ---------\n" +
      rows.map(([k, v]) => `${k}: ${v}`).join("\n") +
      `\n\n${body}`,
    html:
      "<br><br><div>---------- Forwarded message ---------<br>" +
      rows.map(([k, v]) => `<b>${k}:</b> ${escapeHtml(v)}`).join("<br>") +
      `</div><br>${textToHtml(body)}`,
  };
};

const FONT_FAMILIES = [
  "Arial",
  "Times New Roman",
  "Georgia",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Courier New",
];

// pt size -> execCommand fontSize level (1-7)
const FONT_SIZES = [
  { pt: 8, level: 1 },
  { pt: 10, level: 2 },
  { pt: 12, level: 3 },
  { pt: 14, level: 4 },
  { pt: 18, level: 5 },
  { pt: 24, level: 6 },
  { pt: 36, level: 7 },
];

const TABLE_HTML =
  '<table style="border-collapse:collapse;width:100%"><tbody>' +
  Array.from({ length: 3 })
    .map(
      () =>
        "<tr>" +
        Array.from({ length: 3 })
          .map(
            () =>
              '<td style="border:1px solid #cbd2e0;padding:6px;">&nbsp;</td>',
          )
          .join("") +
        "</tr>",
    )
    .join("") +
  "</tbody></table><p></p>";

// Gmail style colour palette (text colour + background colour)
const GRAY_ROW = [
  "#000000",
  "#434343",
  "#666666",
  "#999999",
  "#b7b7b7",
  "#cccccc",
  "#efefef",
  "#f3f3f3",
];

const BRIGHT_ROW = [
  "#ff0000",
  "#ff9900",
  "#ffff00",
  "#00ff00",
  "#00ffff",
  "#0000ff",
  "#9900ff",
  "#ff00ff",
];

const SHADE_ROWS = [
  [
    "#f4cccc",
    "#fce5cd",
    "#fff2cc",
    "#d9ead3",
    "#d0e0e3",
    "#c9daf8",
    "#d9d2e9",
    "#ead1dc",
  ],
  [
    "#ea9999",
    "#f9cb9c",
    "#ffe599",
    "#b6d7a8",
    "#a2c4c9",
    "#a4c2f4",
    "#b4a7d6",
    "#d5a6bd",
  ],
  [
    "#e06666",
    "#f6b26b",
    "#ffd966",
    "#93c47d",
    "#76a5af",
    "#6d9eeb",
    "#8e7cc3",
    "#c27ba0",
  ],
  [
    "#cc0000",
    "#e69138",
    "#f1c232",
    "#6aa84f",
    "#45818e",
    "#3c78d8",
    "#674ea7",
    "#a64d79",
  ],
  [
    "#990000",
    "#b45f06",
    "#bf9000",
    "#38761d",
    "#134f5c",
    "#1155cc",
    "#351c75",
    "#741b47",
  ],
  [
    "#660000",
    "#783f04",
    "#7f6000",
    "#274e13",
    "#0c343d",
    "#1c4587",
    "#20124d",
    "#4c1130",
  ],
];

const PALETTE_ROWS = [GRAY_ROW, BRIGHT_ROW, ...SHADE_ROWS];

function ColorPicker({ onPick }) {
  const [open, setOpen] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const boxRef = useRef(null);

  // click outside -> close
  useEffect(() => {
    if (!open) return undefined;

    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", close);

    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const pick = (command, color) => {
    onPick(command, color);

    if (command === "foreColor") setTextColor(color);

    setOpen(false);
  };

  // swatches must not steal the text selection
  const keep = (event) => event.preventDefault();

  const column = (command, title) => (
    <div className="erp-cp-col">
      <div className="erp-cp-title">{title}</div>

      {PALETTE_ROWS.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`erp-cp-row ${rowIndex === 2 ? "after-gap" : ""}`}
        >
          {row.map((color) => (
            <button
              key={color}
              type="button"
              className="erp-cp-swatch"
              style={{ background: color }}
              title={color}
              onMouseDown={keep}
              onClick={() => pick(command, color)}
            />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="erp-cp" ref={boxRef}>
      <button
        type="button"
        className="erp-rte-btn erp-cp-trigger"
        title="Text colour / Background colour"
        onMouseDown={keep}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="erp-cp-a">
          <b>A</b>
          <i style={{ background: textColor }} />
        </span>

        <ChevronDown size={13} />
      </button>

      {open && (
        <div className="erp-cp-pop">
          {column("hiliteColor", "Background colour")}
          {column("foreColor", "Text colour")}
        </div>
      )}
    </div>
  );
}

// ====================================================
// PROJECT EMAIL DROPDOWN (client emails of ERP projects)
// ====================================================

function ProjectEmailPicker({ options, loading, selectedEmails, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  // click outside -> close
  useEffect(() => {
    if (!open) return undefined;

    const close = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", close);

    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();

    if (!text) return options;

    return options.filter((item) =>
      [item.project, item.client, item.email]
        .join(" ")
        .toLowerCase()
        .includes(text),
    );
  }, [options, query]);

  return (
    <div className="erp-pp" ref={boxRef}>
      <button
        type="button"
        className="erp-pp-btn"
        title="Choose from project emails"
        onClick={() => setOpen((value) => !value)}
      >
        <Folder size={14} />
        Projects
        <ChevronDown size={13} />
      </button>

      {open && (
        <div className="erp-pp-pop">
          <input
            className="erp-pp-search"
            type="text"
            autoFocus
            value={query}
            placeholder="Search project / client / email"
            onChange={(event) => setQuery(event.target.value)}
          />

          <div className="erp-pp-list">
            {loading && <div className="erp-pp-empty">Loading projects...</div>}

            {!loading && filtered.length === 0 && (
              <div className="erp-pp-empty">
                {options.length === 0
                  ? "No project emails found."
                  : "No matching project emails."}
              </div>
            )}

            {!loading &&
              filtered.map((item) => {
                const added = selectedEmails.includes(item.email);

                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`erp-pp-item ${added ? "added" : ""}`}
                    onClick={() => onPick(item)}
                  >
                    {/* mail id on top, project name (+ client) below */}
                    <span className="erp-pp-project">{item.email}</span>

                    <span className="erp-pp-email">
                      {item.project}
                      {item.client ? ` · ${item.client}` : ""}
                    </span>

                    {added && <Check size={14} className="erp-pp-check" />}
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// ====================================================
// RECIPIENT FIELD (Gmail style chips + project dropdown)
// value is still a comma separated string ("a@x.com, b@y.com")
// chips can be dragged between To and Cc
// ====================================================

const DRAG_TYPE = "application/x-erp-recipient";

function RecipientField({
  id,
  field, // "to" | "cc" (used for drag & drop)
  value,
  onChange,
  placeholder,
  options,
  loading,
  onPickProject,
  onMoveEmail,
  onChipDragStart,
}) {
  const [text, setText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [dragging, setDragging] = useState("");
  const inputRef = useRef(null);

  const emails = useMemo(() => uniqueAddresses(value), [value]);

  const commitText = () => {
    const found = addressList(text.trim().replace(/\s+/g, ","));

    if (!found.length) return false;

    onChange(uniqueAddresses(emails, found).join(", "));
    setText("");

    return true;
  };

  const removeEmail = (email) => {
    onChange(emails.filter((item) => item !== email).join(", "));
  };

  const onKeyDown = (event) => {
    if (["Enter", ",", ";", " ", "Tab"].includes(event.key)) {
      if (text.trim() && commitText()) event.preventDefault();
      return;
    }

    if (event.key === "Backspace" && !text && emails.length) {
      event.preventDefault();
      removeEmail(emails[emails.length - 1]);
    }
  };

  // ---------- drag & drop ----------

  const onChipDrag = (event, email) => {
    event.dataTransfer.setData(
      DRAG_TYPE,
      JSON.stringify({ email, from: field }),
    );
    event.dataTransfer.setData("text/plain", email);
    event.dataTransfer.effectAllowed = "move";

    setDragging(email);

    if (onChipDragStart) onChipDragStart();
  };

  const onAreaDragOver = (event) => {
    if (!Array.from(event.dataTransfer.types || []).includes(DRAG_TYPE)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    if (!dragOver) setDragOver(true);
  };

  const onAreaDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDragOver(false);
    }
  };

  const onAreaDrop = (event) => {
    event.preventDefault();
    setDragOver(false);

    let data = null;

    try {
      data = JSON.parse(event.dataTransfer.getData(DRAG_TYPE));
    } catch (err) {
      return;
    }

    if (!data?.email || !data.from || data.from === field) return;

    if (onMoveEmail) onMoveEmail(data.email, data.from, field);
  };

  return (
    <>
      <div
        className={`erp-rcpt ${dragOver ? "drag-over" : ""}`}
        onClick={() => inputRef.current?.focus()}
        onDragOver={onAreaDragOver}
        onDragLeave={onAreaDragLeave}
        onDrop={onAreaDrop}
      >
        {emails.map((email) => (
          <span
            className={`erp-rcpt-chip ${dragging === email ? "dragging" : ""}`}
            key={email}
            title={`${email} (drag to move between To / Cc)`}
            draggable
            onDragStart={(event) => onChipDrag(event, email)}
            onDragEnd={() => setDragging("")}
          >
            <span
              className="erp-rcpt-avatar"
              style={{ backgroundColor: colorFor(email) }}
            >
              {email[0].toUpperCase()}
            </span>

            <span className="erp-rcpt-text">{email}</span>

            <button
              type="button"
              className="erp-rcpt-remove"
              title="Remove"
              onClick={(event) => {
                event.stopPropagation();
                removeEmail(email);
              }}
            >
              <X size={13} />
            </button>
          </span>
        ))}

        <input
          id={id}
          ref={inputRef}
          className="erp-rcpt-input"
          type="text"
          value={text}
          placeholder={emails.length ? "" : placeholder}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commitText}
        />
      </div>

      <ProjectEmailPicker
        options={options}
        loading={loading}
        selectedEmails={emails}
        onPick={onPickProject}
      />
    </>
  );
}

function RichEditor({
  initialHtml,
  onChange,
  focusAtTop,
  minHeight,
  maxImageKB = 2048, // biggest allowed size AFTER the picture is shrunk
  imageStoreWidth = 1200, // pictures are shrunk to this many px wide
  imageDefaultWidth = 480, // size shown when a picture is first inserted
}) {
  const areaRef = useRef(null);
  const savedRange = useRef(null);
  const imageInput = useRef(null);

  const [sourceMode, setSourceMode] = useState(false);
  const [sourceText, setSourceText] = useState("");

  // picture that is selected (click on it) -> resize / remove toolbar
  const rootRef = useRef(null);
  const selImg = useRef(null);
  const [imgBox, setImgBox] = useState(null);
  const [widthText, setWidthText] = useState("");

  const clearImg = () => {
    selImg.current = null;
    setImgBox(null);
  };

  // place the outline + toolbar on top of the selected picture
  const updateImgBox = () => {
    const img = selImg.current;
    const root = rootRef.current;

    if (!img || !root || !img.isConnected) {
      clearImg();
      return;
    }

    const imageRect = img.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    const areaRect = (areaRef.current || root).getBoundingClientRect();

    // only the part of the picture that is visible inside the editor
    // is outlined (the rest is scrolled out of view)
    const top = Math.max(imageRect.top, areaRect.top);
    const left = Math.max(imageRect.left, areaRect.left);
    const bottom = Math.min(imageRect.bottom, areaRect.bottom);
    const right = Math.min(imageRect.right, areaRect.right);

    setImgBox({
      top: top - rootRect.top,
      left: left - rootRect.left,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
      visible: bottom > top && right > left,
    });

    setWidthText(String(Math.round(imageRect.width)));
  };

  useEffect(() => {
    const el = areaRef.current;

    if (!el) return;

    el.innerHTML = initialHtml || "";

    if (focusAtTop) {
      el.focus();

      const range = document.createRange();
      range.setStart(el, 0);
      range.collapse(true);

      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      el.closest("form")?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
    // run only once when the editor mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    const el = areaRef.current;

    if (el) onChange(el.innerHTML, el.innerText);

    if (selImg.current) updateImgBox();
  };

  const setImgWidth = (value) => {
    const img = selImg.current;

    if (!img) return;

    const width = Math.max(16, Math.min(1200, Math.round(value)));

    img.setAttribute("width", String(width));
    img.removeAttribute("height");
    img.style.width = `${width}px`;
    img.style.height = "auto";
    img.style.maxWidth = "100%";

    emit();
    updateImgBox();
  };

  const applyWidthText = () => {
    const number = Number(widthText);

    if (Number.isFinite(number) && number >= 16) setImgWidth(number);
  };

  const removeImg = () => {
    selImg.current?.remove();
    clearImg();
    emit();
  };

  const onAreaClick = (event) => {
    if (event.target.tagName === "IMG") {
      selImg.current = event.target;
      updateImgBox();
    } else if (selImg.current) {
      clearImg();
    }
  };

  const saveSelection = () => {
    const sel = window.getSelection();

    if (sel && sel.rangeCount && areaRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const el = areaRef.current;

    if (!el) return;

    el.focus();

    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const exec = (command, value = null) => {
    restoreSelection();

    document.execCommand("styleWithCSS", false, true);
    document.execCommand(command, false, value);

    saveSelection();
    emit();
  };

  const addLink = () => {
    saveSelection();

    const url = window.prompt("Link URL", "https://");

    if (!url || !url.trim()) return;

    exec(
      "createLink",
      /^(https?:|mailto:)/i.test(url.trim())
        ? url.trim()
        : `https://${url.trim()}`,
    );
  };

  const addImage = (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) return;

    if (
      !/^image\/(png|jpe?g|gif|webp)$/i.test(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      window.alert("Please choose a PNG, JPG, GIF or WebP image (max 10 MB).");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const picture = new window.Image();

    picture.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // shrink big pictures first (smaller mail, sharp enough for screens)
      const scale = Math.min(1, imageStoreWidth / picture.naturalWidth);
      const width = Math.max(1, Math.round(picture.naturalWidth * scale));
      const height = Math.max(1, Math.round(picture.naturalHeight * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      const isJpeg = /jpe?g/i.test(file.type);

      if (isJpeg) {
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, width, height);
      }

      context.drawImage(picture, 0, 0, width, height);

      const dataUrl = canvas.toDataURL(
        isJpeg ? "image/jpeg" : "image/png",
        0.85,
      );

      // data URL text is about 1.37x bigger than the picture itself
      if (dataUrl.length > maxImageKB * 1024 * 1.37) {
        window.alert(
          `This picture is still too big (limit ${maxImageKB} KB). Please use a smaller or simpler picture.`,
        );
        return;
      }

      const shown = Math.min(width, imageDefaultWidth);

      exec(
        "insertHTML",
        `<img src="${dataUrl}" alt="" width="${shown}" style="width:${shown}px;max-width:100%;height:auto;" />`,
      );
    };

    picture.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      window.alert("Unable to read this image.");
    };

    picture.src = objectUrl;
  };

  const toggleSource = () => {
    if (!sourceMode) {
      clearImg();
      setSourceText(areaRef.current.innerHTML);
      setSourceMode(true);
    } else {
      areaRef.current.innerHTML = sourceText;
      setSourceMode(false);
      emit();
    }
  };

  // toolbar buttons must not steal the text selection
  const keepSelection = (event) => event.preventDefault();

  const Btn = ({ title, onClick, children, active }) => (
    <button
      type="button"
      className={`erp-rte-btn ${active ? "active" : ""}`}
      title={title}
      onMouseDown={keepSelection}
      onClick={onClick}
    >
      {children}
    </button>
  );

  return (
    <div className="erp-rte" ref={rootRef}>
      <div className="erp-rte-toolbar">
        <Btn title="Insert link" onClick={addLink}>
          <Link2 size={16} />
        </Btn>

        <Btn title="Insert image" onClick={() => imageInput.current?.click()}>
          <ImageIcon size={16} />
        </Btn>

        <input
          ref={imageInput}
          type="file"
          accept="image/*"
          hidden
          onChange={addImage}
        />

        <span className="erp-rte-sep" />

        <Btn title="Bold (Ctrl+B)" onClick={() => exec("bold")}>
          <Bold size={16} />
        </Btn>

        <Btn title="Italic (Ctrl+I)" onClick={() => exec("italic")}>
          <Italic size={16} />
        </Btn>

        <Btn title="Underline (Ctrl+U)" onClick={() => exec("underline")}>
          <Underline size={16} />
        </Btn>

        <span className="erp-rte-sep" />

        <ColorPicker onPick={exec} />

        <span className="erp-rte-sep" />

        <select
          className="erp-rte-select"
          title="Formats"
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) {
              exec("formatBlock", `<${event.target.value}>`);
            }
            event.target.value = "";
          }}
        >
          <option value="">Formats</option>
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="blockquote">Quote</option>
          <option value="pre">Code block</option>
        </select>

        <select
          className="erp-rte-select"
          title="Font"
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) exec("fontName", event.target.value);
            event.target.value = "";
          }}
        >
          <option value="">Font</option>
          {FONT_FAMILIES.map((font) => (
            <option key={font} value={font} style={{ fontFamily: font }}>
              {font}
            </option>
          ))}
        </select>

        <select
          className="erp-rte-select small"
          title="Font size"
          defaultValue=""
          onChange={(event) => {
            if (event.target.value) exec("fontSize", event.target.value);
            event.target.value = "";
          }}
        >
          <option value="">Size</option>
          {FONT_SIZES.map((size) => (
            <option key={size.pt} value={size.level}>
              {size.pt}pt
            </option>
          ))}
        </select>

        <span className="erp-rte-sep" />

        <Btn title="Align left" onClick={() => exec("justifyLeft")}>
          <AlignLeft size={16} />
        </Btn>

        <Btn title="Align center" onClick={() => exec("justifyCenter")}>
          <AlignCenter size={16} />
        </Btn>

        <Btn title="Align right" onClick={() => exec("justifyRight")}>
          <AlignRight size={16} />
        </Btn>

        <Btn title="Justify" onClick={() => exec("justifyFull")}>
          <AlignJustify size={16} />
        </Btn>

        <span className="erp-rte-sep" />

        <Btn title="Bullet list" onClick={() => exec("insertUnorderedList")}>
          <List size={16} />
        </Btn>

        <Btn title="Numbered list" onClick={() => exec("insertOrderedList")}>
          <ListOrdered size={16} />
        </Btn>

        <Btn title="Decrease indent" onClick={() => exec("outdent")}>
          <IndentDecrease size={16} />
        </Btn>

        <Btn title="Increase indent" onClick={() => exec("indent")}>
          <IndentIncrease size={16} />
        </Btn>

        <span className="erp-rte-sep" />

        <Btn
          title="Insert table"
          onClick={() => exec("insertHTML", TABLE_HTML)}
        >
          <TableIcon size={16} />
        </Btn>

        <Btn title="Clear formatting" onClick={() => exec("removeFormat")}>
          <RemoveFormatting size={16} />
        </Btn>

        <Btn title="HTML source" active={sourceMode} onClick={toggleSource}>
          <Code size={16} />
        </Btn>
      </div>

      <div
        ref={areaRef}
        className="erp-rte-area"
        role="textbox"
        aria-multiline="true"
        aria-label="Message"
        contentEditable
        suppressContentEditableWarning
        style={{ minHeight, display: sourceMode ? "none" : "block" }}
        onInput={emit}
        onClick={onAreaClick}
        onScroll={updateImgBox}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onBlur={saveSelection}
      />

      {sourceMode && (
        <textarea
          className="erp-rte-source"
          style={{ minHeight }}
          value={sourceText}
          spellCheck={false}
          onChange={(event) => setSourceText(event.target.value)}
        />
      )}

      {/* selected picture: outline + resize / remove toolbar */}
      {imgBox && !sourceMode && (
        <>
          {imgBox.visible && (
            <div
              className="erp-img-outline"
              style={{
                top: imgBox.top,
                left: imgBox.left,
                width: imgBox.width,
                height: imgBox.height,
              }}
            />
          )}

          <div className="erp-img-tools">
            {[
              ["Small", 0.25],
              ["Medium", 0.5],
              ["Large", 0.75],
              ["Original", 1],
            ].map(([label, ratio]) => (
              <button
                key={label}
                type="button"
                title={`${label} (${ratio * 100}% of original size)`}
                onClick={() =>
                  setImgWidth(
                    (selImg.current?.naturalWidth || imgBox.width) * ratio,
                  )
                }
              >
                {label}
              </button>
            ))}

            <span className="erp-img-sep" />

            <input
              type="number"
              min="16"
              max="1200"
              title="Width in pixels - press Enter"
              value={widthText}
              onChange={(event) => setWidthText(event.target.value)}
              onBlur={applyWidthText}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  applyWidthText();
                }
              }}
            />

            <span>px</span>

            <span className="erp-img-sep" />

            <button
              type="button"
              className="danger"
              title="Remove image"
              onClick={removeImg}
            >
              <Trash2 size={14} /> Remove
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function EmailPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const routeFolder = location.pathname.split("/")[2] || "inbox";

  const activeFolder = decodeURIComponent(routeFolder);

  const [emails, setEmails] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);

  const [folders, setFolders] = useState([]);
  const [mailboxEmail, setMailboxEmail] = useState("");

  // SDC-MULTIMAIL: extra mailboxes + the one that is open ("" = main)
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState("");

  // coming back to this page always starts on the main mailbox
  useEffect(() => {
    currentAccount = "";

    return () => {
      currentAccount = "";
    };
  }, []);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const [showSettings, setShowSettings] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [composeOpen, setComposeOpen] = useState(false);
  // new-message window: normal | min (title bar only) | full (big, centred)
  const [composeSize, setComposeSize] = useState("normal");
  const [compose, setCompose] = useState(emptyCompose);
  const [composeFiles, setComposeFiles] = useState([]);
  const [composeMode, setComposeMode] = useState("compose");
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState("");
  const [composeSession, setComposeSession] = useState(0);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showQuote, setShowQuote] = useState(false);

  // client emails of ERP projects (for the "To" / "Cc" dropdown)
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // signature (saved one + the copy being edited in Settings)
  const [signature, setSignature] = useState({ html: "", enabled: true });
  const [sigDraft, setSigDraft] = useState({ html: "", enabled: true });
  const [sigSaving, setSigSaving] = useState(false);
  const [sigMessage, setSigMessage] = useState(null);
  const composeDirty = useRef(false);
  const latestCompose = useRef({});

  // speed: mails already opened (or hovered) open instantly
  const detailCache = useRef(new Map());
  const detailInflight = useRef(new Map());
  const attachmentCache = useRef(new Map());
  const hoverTimer = useRef(null);

  // Inbox + Sent list, used to find the other mails of a conversation
  const threadCache = useRef(null);
  const [related, setRelated] = useState([]);

  const [attachmentBusy, setAttachmentBusy] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  const refreshFolder = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  // ====================================================
  // MAILBOX IDENTITY
  // ====================================================

  useEffect(() => {
    const controller = new AbortController();

    apiRequest("/me", { signal: controller.signal })
      .then((result) => {
        if (!controller.signal.aborted) {
          setMailboxEmail(result.mailbox?.email || "");
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Mailbox identity:", err.message);
        }
      });

    return () => controller.abort();
  }, []);

  // ====================================================
  // EXTRA MAIL ACCOUNTS (SDC-MULTIMAIL)
  // ====================================================

  useEffect(() => {
    const controller = new AbortController();

    apiRequest("/accounts", { signal: controller.signal })
      .then((result) => {
        if (!controller.signal.aborted) {
          setAccounts(Array.isArray(result.accounts) ? result.accounts : []);
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Mail accounts:", err.message);
        }
      });

    return () => controller.abort();
  }, []);

  // ====================================================
  // PROJECT EMAILS (project name + client emails)
  // ====================================================

  useEffect(() => {
    let cancelled = false;

    setProjectsLoading(true);

    api
      .get("/projects")
      .then(({ data }) => {
        if (cancelled) return;

        const options = [];

        (Array.isArray(data) ? data : []).forEach((project) => {
          const emails = [
            ...new Set(
              [project.clientEmail, ...(project.clientEmails || [])]
                .map((email) =>
                  String(email || "")
                    .trim()
                    .toLowerCase(),
                )
                .filter(Boolean),
            ),
          ];

          emails.forEach((email) => {
            options.push({
              key: `${project._id}:${email}`,
              projectId: String(project._id),
              email,
              project: project.name || "(Unnamed project)",
              client: project.clientName || "",
            });
          });
        });

        options.sort((a, b) => a.project.localeCompare(b.project));

        setProjectOptions(options);
      })
      .catch((err) => {
        console.error("Project emails:", err.message);
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ====================================================
  // LOAD FOLDERS
  // ====================================================

  const loadFolders = useCallback(async () => {
    const forAccount = currentAccount;

    try {
      const result = await apiRequest("/folders");

      // answer of an account that is no longer open -> ignore
      if (forAccount !== currentAccount) return;

      setFolders(Array.isArray(result.folders) ? result.folders : []);
    } catch (err) {
      if (forAccount === currentAccount) setError(err.message);
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders, refreshKey]);

  const customFolders = useMemo(
    () =>
      folders.filter(
        (folder) =>
          !folder.system &&
          !SYSTEM_IDS.has(folder.id) &&
          folder.selectable !== false,
      ),
    [folders],
  );

  // count badge from the folders API (if the API sends unread / count / total)
  const folderCount = (folder) => {
    const n = Number(folder?.unread ?? folder?.count ?? folder?.total ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  // ====================================================
  // LOAD MESSAGES
  // ====================================================

  const loadFolder = useCallback(async (folder, signal) => {
    setLoading(true);
    setError("");
    setEmails([]);
    setSelectedId(null);
    setCheckedIds([]);
    setDetail(null);

    try {
      let messages = [];

      if (folder === "starred") {
        const result = await apiRequest("/folders");

        const available = Array.isArray(result.folders)
          ? result.folders.filter(
              (item) => item.selectable !== false && item.id !== "starred",
            )
          : [];

        const results = await Promise.allSettled(
          available.map(async (item) => {
            const response = await apiRequest(
              `/folders/messages${folderQuery(item.id)}`,
              { signal },
            );

            return (response.messages || []).map((message) =>
              normalizeMessage(message, item.id),
            );
          }),
        );

        const failures = results.filter(
          (result) => result.status === "rejected",
        );

        messages = results
          .filter((result) => result.status === "fulfilled")
          .flatMap((result) => result.value)
          .filter((message) => message.starred);

        if (failures.length && !messages.length) {
          throw failures[0].reason;
        }
      } else {
        const path =
          folder === "inbox" || folder === "sent"
            ? `/${folder}`
            : `/folders/messages${folderQuery(folder)}`;

        const result = await apiRequest(path, { signal });

        messages = (result.messages || []).map((message) =>
          normalizeMessage(message, folder),
        );
      }

      if (signal.aborted) return;

      messages.sort(
        (a, b) =>
          new Date(b.internalDate || b.date).getTime() -
          new Date(a.internalDate || a.date).getTime(),
      );

      setEmails(messages);
      setSelectedId(null);
    } catch (err) {
      if (err.name !== "AbortError" && !signal.aborted) {
        setError(err.message || "Unable to load emails.");
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    loadFolder(activeFolder, controller.signal);

    return () => controller.abort();
  }, [activeFolder, refreshKey, loadFolder]);

  // ====================================================
  // SEARCH AND SELECTION
  // ====================================================

  const filteredEmails = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return emails;

    return emails.filter((email) =>
      [email.sender, email.email, email.subject, email.preview]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [emails, search]);

  // nothing is open until the user clicks a mail (like Gmail)
  const selectedEmail =
    filteredEmails.find((email) => email.key === selectedId) || null;

  const unreadCount =
    activeFolder === "inbox"
      ? emails.filter((email) => email.unread).length
      : 0;

  const selectedMessages = useMemo(() => {
    const checked = filteredEmails.filter((email) =>
      checkedIds.includes(email.key),
    );

    return checked.length ? checked : selectedEmail ? [selectedEmail] : [];
  }, [checkedIds, filteredEmails, selectedEmail]);

  const toggleChecked = (key) => {
    setCheckedIds((previous) =>
      previous.includes(key)
        ? previous.filter((item) => item !== key)
        : [...previous, key],
    );
  };

  const openFolder = (folder) => {
    setSearch("");
    setCheckedIds([]);
    setSelectedId(null);
    setDetail(null);
    setError("");

    navigate(`/email/${encodeURIComponent(folder)}`);
  };

  // ====================================================
  // LOAD MESSAGE DETAILS
  // ====================================================

  // one mail's details: cached + de-duplicated (also used for hover prefetch)
  const fetchDetail = useCallback((email) => {
    const cached = detailCache.current.get(email.key);

    if (cached) return Promise.resolve(cached);

    const running = detailInflight.current.get(email.key);

    if (running) return running;

    // SDC-MULTIMAIL: remember which account this request belongs to
    const forAccount = currentAccount;

    const request = apiRequest(
      `/folders/messages/${encodeURIComponent(email.uid)}${folderQuery(
        email.folder,
      )}`,
    )
      .then((response) => {
        if (!response.message) throw new Error("Email details not found.");

        const cache = detailCache.current;

        // never cache the answer of an account that is no longer open
        if (forAccount === currentAccount) {
          cache.set(email.key, response.message);
        }

        // keep memory small: forget the oldest after 60 mails
        if (cache.size > 60) cache.delete(cache.keys().next().value);

        return response.message;
      })
      .finally(() => detailInflight.current.delete(email.key));

    detailInflight.current.set(email.key, request);

    return request;
  }, []);

  // start loading a mail when the mouse rests on it for a moment
  const prefetchDetail = (email) => {
    clearTimeout(hoverTimer.current);

    hoverTimer.current = setTimeout(() => {
      fetchDetail(email).catch(() => {});
    }, 300);
  };

  const cancelPrefetch = () => clearTimeout(hoverTimer.current);

  // when a mail is opened, mark it as read (list + sidebar count + server)
  const markOpenedAsRead = (email) => {
    if (!email?.unread) return;

    // show as read immediately (also updates the Inbox count badge)
    setEmails((previous) =>
      previous.map((item) =>
        item.key === email.key ? { ...item, unread: false } : item,
      ),
    );

    // save the \Seen flag on the mail server
    apiRequest(`/messages/${email.uid}/read`, {
      method: "PATCH",
      body: { folder: email.folder, read: true },
    })
      .then(() => loadFolders())
      .catch(() => {
        // failed -> put it back as unread
        setEmails((previous) =>
          previous.map((item) =>
            item.key === email.key ? { ...item, unread: true } : item,
          ),
        );
      });
  };

  useEffect(() => {
    setDetailError("");

    if (!selectedEmail) {
      setDetail(null);
      setDetailLoading(false);
      return;
    }

    // opening a mail marks it as read
    markOpenedAsRead(selectedEmail);

    const cached = detailCache.current.get(selectedEmail.key);

    // already opened before -> show instantly, no loading
    if (cached) {
      setDetail(cached);
      setDetailLoading(false);
      return;
    }

    let cancelled = false;

    setDetail(null);
    setDetailLoading(true);

    fetchDetail(selectedEmail)
      .then((message) => {
        if (!cancelled) setDetail(message);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(err.message);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail?.key]);

  // ====================================================
  // CONVERSATION HISTORY (other mails with the same subject)
  // ====================================================

  // Inbox + Sent list of the open mailbox (fetched once, then reused)
  const getThreadPool = useCallback(() => {
    const cached = threadCache.current;

    if (cached && cached.account === currentAccount) return cached.promise;

    const account = currentAccount;

    const promise = Promise.all(
      ["inbox", "sent"].map(async (folder) => {
        try {
          const result = await apiRequest(`/${folder}`);

          return (result.messages || []).map((message) =>
            normalizeMessage(message, folder),
          );
        } catch (err) {
          return [];
        }
      }),
    ).then((groups) => groups.flat());

    threadCache.current = { account, promise };

    return promise;
  }, []);

  // new mails may have arrived -> list is fetched again next time
  useEffect(() => {
    threadCache.current = null;
  }, [refreshKey]);

  useEffect(() => {
    setRelated([]);

    if (!selectedEmail) return undefined;

    const wanted = threadSubject(selectedEmail.subject);

    if (!wanted || wanted === "(no subject)") return undefined;

    let cancelled = false;

    getThreadPool().then((pool) => {
      if (cancelled) return;

      const items = pool
        .filter(
          (message) =>
            message.key !== selectedEmail.key &&
            threadSubject(message.subject) === wanted,
        )
        .sort(
          (a, b) =>
            (new Date(b.internalDate || b.date).getTime() || 0) -
            (new Date(a.internalDate || a.date).getTime() || 0),
        );

      setRelated(items);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail?.key, refreshKey]);

  // ====================================================
  // EMAIL OPERATIONS
  // ====================================================

  const runOperation = async (path, body, successMessage, method = "POST") => {
    setBusy(true);
    setError("");
    setNotice("");

    try {
      await apiRequest(path, { method, body });

      setNotice(successMessage);
      refreshFolder();

      return true;
    } catch (err) {
      setError(err.message || "Email operation failed.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const runGroupedOperation = async (path, messages, successMessage) => {
    if (!messages.length) return;

    const groups = new Map();

    for (const message of messages) {
      if (!groups.has(message.folder)) {
        groups.set(message.folder, []);
      }

      groups.get(message.folder).push(Number(message.uid));
    }

    setBusy(true);
    setError("");
    setNotice("");

    try {
      for (const [folder, uids] of groups) {
        await apiRequest(path, {
          method: "POST",
          body: { folder, uids },
        });
      }

      setNotice(successMessage);
      refreshFolder();
    } catch (err) {
      setError(
        `${err.message}. Refresh the folder to verify which emails changed.`,
      );
    } finally {
      setBusy(false);
    }
  };

  const toggleStar = async (message) => {
    await runOperation(
      `/messages/${message.uid}/star`,
      {
        folder: message.folder,
        starred: !message.starred,
      },
      message.starred ? "Star removed." : "Email starred.",
      "PATCH",
    );
  };

  const toggleRead = async (message) => {
    await runOperation(
      `/messages/${message.uid}/read`,
      {
        folder: message.folder,
        read: message.unread,
      },
      message.unread ? "Marked as read." : "Marked as unread.",
      "PATCH",
    );
  };

  const deleteSelected = async () => {
    if (!selectedMessages.length) return;

    if (activeFolder === "trash") {
      if (
        !window.confirm(
          "Permanently delete selected emails? This cannot be undone.",
        )
      ) {
        return;
      }

      await runGroupedOperation(
        "/messages/permanent-delete",
        selectedMessages,
        "Emails permanently deleted.",
      );

      return;
    }

    await runGroupedOperation(
      "/messages/delete",
      selectedMessages,
      "Emails moved to Trash.",
    );
  };

  const archiveSelected = async () => {
    await runGroupedOperation(
      "/messages/archive",
      selectedMessages,
      "Emails archived.",
    );
  };

  const restoreSelected = async () => {
    await runGroupedOperation(
      "/messages/restore",
      selectedMessages,
      "Emails restored to Inbox.",
    );
  };

  const moveSelected = async () => {
    if (!selectedMessages.length) return;

    const destination = window.prompt(
      "Move to folder (e.g. inbox, sent, archive or custom folder name):",
    );

    if (!destination?.trim()) return;

    const groups = new Map();

    selectedMessages.forEach((message) => {
      if (!groups.has(message.folder)) {
        groups.set(message.folder, []);
      }

      groups.get(message.folder).push(Number(message.uid));
    });

    setBusy(true);
    setError("");

    try {
      for (const [folder, uids] of groups) {
        await apiRequest("/messages/move", {
          method: "POST",
          body: {
            folder,
            uids,
            destination: destination.trim(),
          },
        });
      }

      setNotice("Emails moved successfully.");
      refreshFolder();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ====================================================
  // CUSTOM FOLDER MANAGEMENT
  // ====================================================

  const createFolder = async () => {
    const name = window.prompt("Enter new folder name:");

    if (!name?.trim()) return;

    const success = await runOperation(
      "/folders",
      { name: name.trim() },
      "Folder created.",
    );

    if (success) loadFolders();
  };

  const renameFolder = async (folder) => {
    const newName = window.prompt("Rename folder:", folder.name);

    if (!newName?.trim() || newName === folder.name) return;

    const success = await runOperation(
      "/folders",
      {
        oldName: folder.path || folder.name,
        newName: newName.trim(),
      },
      "Folder renamed.",
      "PATCH",
    );

    if (success) {
      openFolder("inbox");
      loadFolders();
    }
  };

  const removeFolder = async (folder) => {
    if (
      !window.confirm(
        `Delete folder "${folder.name}"? The folder must be empty.`,
      )
    ) {
      return;
    }

    const success = await runOperation(
      "/folders",
      { name: folder.path || folder.name },
      "Folder deleted.",
      "DELETE",
    );

    if (success) {
      openFolder("inbox");
      loadFolders();
    }
  };

  // ====================================================
  // COMPOSE / REPLY / REPLY ALL / FORWARD
  // ====================================================

  const applyCompose = (next) => {
    setCompose({ ...emptyCompose, ...next });
    setShowCc(Boolean(next.cc && next.cc.trim()));
    setShowBcc(Boolean(next.bcc && next.bcc.trim()));
    setShowQuote(false);
    setComposeSize("normal");
    setComposeOpen(true);
  };

  const openCompose = (mode = "compose") => {
    composeDirty.current = false;
    setComposeSession((n) => n + 1);
    setComposeMode(mode);
    setComposeFiles([]);
    setComposeError("");

    const sigBlock = signatureBlock(signature);

    const sigText = sigBlock ? `\n\n-- \n${htmlToPlain(signature.html)}` : "";

    if (mode === "compose") {
      applyCompose({ html: sigBlock });
      return;
    }

    if (!detail || !selectedEmail) {
      setError("Please wait until email details load.");
      return;
    }

    // SDC-MULTIMAIL: "me" is the mailbox that is open right now
    const self = (activeAccount || mailboxEmail).toLowerCase();

    const senderAddresses = uniqueAddresses(
      detail.replyToAddresses?.length
        ? detail.replyToAddresses
        : detail.fromAddresses?.length
          ? detail.fromAddresses
          : detail.from,
    );

    const toAddresses = uniqueAddresses(
      detail.toAddresses?.length ? detail.toAddresses : detail.to,
    );

    const ccAddresses = uniqueAddresses(
      detail.ccAddresses?.length ? detail.ccAddresses : detail.cc,
    );

    const replyingToSent = selectedEmail.folder === "sent";

    const replyTargets = replyingToSent ? toAddresses : senderAddresses;

    if (mode === "reply" || mode === "replyAll") {
      const to = replyTargets.filter((email) => email !== self);

      const cc =
        mode === "replyAll"
          ? uniqueAddresses(senderAddresses, toAddresses, ccAddresses).filter(
              (email) => email !== self && !to.includes(email),
            )
          : [];

      applyCompose({
        to: to.join(", "),
        cc: cc.join(", "),
        subject: subjectWithPrefix(detail.subject, "Re"),
        html: sigBlock,
        body: sigText,
        ...replyQuote(
          asText(detail.from),
          formatDate(detail.date),
          asText(detail.body),
        ),
      });
    } else if (mode === "forward") {
      const parts = forwardParts(detail);

      applyCompose({
        subject: subjectWithPrefix(detail.subject, "Fwd"),
        html: sigBlock + parts.html,
        body: sigText + parts.body,
      });
    } else if (mode === "draft") {
      applyCompose({
        to: asText(detail.to),
        cc: asText(detail.cc),
        subject: asText(detail.subject),
        body: asText(detail.body),
      });
    }
  };

  // ====================================================
  // SAVE DRAFT
  // ====================================================

  const saveDraft = async () => {
    if (sending) return;

    setComposeError("");

    if (composeFiles.length) {
      setComposeError(
        "Draft attachment saving is not supported. Remove attachments before saving.",
      );
      return;
    }

    setSending(true);

    try {
      await apiRequest("/drafts", {
        method: "POST",
        body: buildDraftBody(compose),
      });

      composeDirty.current = false;
      setComposeOpen(false);
      setCompose({ ...emptyCompose });
      setComposeFiles([]);
      setNotice("Draft saved in MailEnable Drafts.");

      refreshFolder();
    } catch (err) {
      setComposeError(err.message);
    } finally {
      setSending(false);
    }
  };

  // ====================================================
  // SEND EMAIL - EXISTING WORKING SMTP API
  // ====================================================

  const sendEmail = async (event) => {
    event.preventDefault();

    if (sending) return;

    setComposeError("");

    if (
      composeFiles.length > 5 ||
      composeFiles.some((file) => file.size > 15 * 1024 * 1024)
    ) {
      setComposeError("Maximum 5 attachments, 15 MB per file.");
      return;
    }

    setSending(true);

    try {
      const data = new FormData();

      data.append("to", compose.to.trim());
      data.append("cc", compose.cc.trim());
      data.append("bcc", compose.bcc.trim());
      data.append("subject", compose.subject.trim());
      data.append("text", fullText(compose));
      data.append("html", fullHtml(compose));

      composeFiles.forEach((file) => {
        data.append("attachments", file);
      });

      const result = await apiRequest("/send", {
        method: "POST",
        body: data,
      });

      composeDirty.current = false;
      setComposeOpen(false);
      setCompose({ ...emptyCompose });
      setComposeFiles([]);

      const message =
        result.sentSaved === true
          ? "Email accepted by SMTP and saved in Sent Items."
          : result.sentSaved === false
            ? "Email accepted by SMTP, but Sent copy was not saved."
            : "Email accepted by SMTP server.";

      setNotice(message);

      if (activeFolder === "sent") {
        refreshFolder();
      }
    } catch (err) {
      setComposeError(err.message || "Unable to send email.");
    } finally {
      setSending(false);
    }
  };

  // ====================================================
  // ATTACHMENT: CARD CLICK = OPEN, DOWNLOAD ICON = DOWNLOAD
  // (works for the open mail and for mails of the conversation history)
  // ====================================================

  const openAttachment = async (
    attachment,
    download = false,
    mail = selectedEmail,
  ) => {
    if (!mail || attachmentBusy) return;

    // pdf / image / text -> open in new tab. Word, Excel etc. -> download
    const shouldDownload = download || !isPreviewable(attachment.contentType);

    const cacheKey = `${mail.key}-${attachment.index}`;

    const tab = !shouldDownload ? window.open("", "_blank") : null;

    // tell the user something is happening while the file is fetched
    if (tab) {
      tab.document.write(
        '<title>Opening...</title><p style="font-family:Arial,sans-serif;padding:24px;color:#475467">Opening document, please wait...</p>',
      );
    }

    setAttachmentBusy(`${cacheKey}-${shouldDownload}`);

    try {
      // the file is fetched once; opening / downloading it again is instant
      let blob = attachmentCache.current.get(cacheKey);

      if (!blob) {
        const response = await apiRequest(
          `/folders/messages/${mail.uid}/attachments/${attachment.index}` +
            `${folderQuery(mail.folder)}&download=0`,
          { raw: true },
        );

        blob = await response.blob();

        const cache = attachmentCache.current;

        cache.set(cacheKey, blob);

        if (cache.size > 5) cache.delete(cache.keys().next().value);
      }

      const typed = new Blob([blob], {
        type: shouldDownload
          ? "application/octet-stream"
          : attachment.contentType || blob.type,
      });

      const url = URL.createObjectURL(typed);

      if (shouldDownload) {
        const anchor = document.createElement("a");

        anchor.href = url;
        anchor.download = attachment.filename || "attachment";

        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else if (tab) {
        tab.location.href = url;
      } else {
        URL.revokeObjectURL(url);
        throw new Error("Please allow popups to preview documents.");
      }
    } catch (err) {
      if (tab && !tab.closed) tab.close();

      alert(err.message || "Unable to open attachment.");
    } finally {
      setAttachmentBusy("");
    }
  };

  // sender name / address split for the detail header
  const fromText = asText(detail?.from || selectedEmail?.sender);
  const fromName = fromText.replace(/\s*<[^>]*>/, "").trim() || fromText;
  const fromAddr = (fromText.match(/<[^>]*>/) || [""])[0];

  // ====================================================
  // GMAIL-STYLE COMPOSER (inline for reply/forward, floating for new mail)
  // ====================================================

  const INLINE_MODES = ["reply", "replyAll", "forward"];
  const isInlineCompose = composeOpen && INLINE_MODES.includes(composeMode);

  // closing a mail that was not sent saves it to Drafts automatically
  const closeCompose = async () => {
    if (sending) return;

    const snapshot = compose;
    const shouldSave = composeDirty.current && hasDraftContent(snapshot);

    composeDirty.current = false;
    setComposeOpen(false);

    if (!shouldSave) return;

    try {
      await apiRequest("/drafts", {
        method: "POST",
        body: buildDraftBody(snapshot),
      });

      setNotice("Mail was not sent - saved to Drafts automatically.");

      if (activeFolder === "drafts") refreshFolder();
    } catch (err) {
      setError(`Could not save draft automatically: ${err.message}`);
    }
  };

  // browser tab closed / page changed while writing -> save draft too
  latestCompose.current = { composeOpen, compose };

  useEffect(() => {
    const flush = () => {
      const { composeOpen: open, compose: current } = latestCompose.current;

      if (!open || !composeDirty.current || !hasDraftContent(current)) return;

      const authToken = getToken();

      if (!authToken) return;

      composeDirty.current = false;

      // SDC-MULTIMAIL: the draft goes to the mailbox that is open
      fetch(`${API}${withAccount("/drafts")}`, {
        method: "POST",
        keepalive: true,
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildDraftBody(current)),
      }).catch(() => {});
    };

    window.addEventListener("beforeunload", flush);

    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, []);

  // switching to another email closes an inline reply / forward box
  useEffect(() => {
    if (composeOpen && INLINE_MODES.includes(composeMode)) closeCompose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail?.key]);

  const composeTitle =
    {
      reply: "Reply",
      replyAll: "Reply all",
      forward: "Forward",
      draft: "Draft",
      compose: "New Message",
    }[composeMode] || "New Message";

  const setField = (key) => (event) => {
    composeDirty.current = true;
    setCompose((value) => ({ ...value, [key]: event.target.value }));
  };

  // To / Cc chips change (add or remove) -> store as comma separated text
  const setRecipients = (key) => (next) => {
    composeDirty.current = true;
    setCompose((value) => ({ ...value, [key]: next }));
  };

  // chip dragged from one field to the other (To <-> Cc)
  const moveRecipient = (email, from, to) => {
    composeDirty.current = true;

    if (to === "cc") setShowCc(true);

    setCompose((value) => {
      const source = uniqueAddresses(value[from]).filter(
        (address) => address !== email.toLowerCase(),
      );

      const target = uniqueAddresses(value[to], [email]);

      return {
        ...value,
        [from]: source.join(", "),
        [to]: target.join(", "),
      };
    });
  };

  // a chip started dragging -> make sure the Cc row is visible to drop on
  const showCcForDrag = () => {
    setTimeout(() => setShowCc(true), 0);
  };

  // project email picked from the dropdown
  // To  -> that email goes to To, the other emails of the same project go to Cc
  // Cc  -> only that email goes to Cc
  const pickProjectEmail = (field) => (item) => {
    composeDirty.current = true;

    const email = item.email.toLowerCase();

    const siblings = projectOptions
      .filter((option) => option.projectId === item.projectId)
      .map((option) => option.email.toLowerCase());

    if (field === "to" && siblings.some((other) => other !== email)) {
      setShowCc(true);
    }

    setCompose((value) => {
      const toList = uniqueAddresses(value.to);
      const ccList = uniqueAddresses(value.cc);

      if (field === "cc") {
        if (toList.includes(email)) return value;

        return { ...value, cc: uniqueAddresses(ccList, [email]).join(", ") };
      }

      const nextTo = uniqueAddresses(toList, [email]);

      const nextCc = uniqueAddresses(ccList, siblings).filter(
        (address) => !nextTo.includes(address),
      );

      return {
        ...value,
        to: nextTo.join(", "),
        cc: nextCc.join(", "),
      };
    });
  };

  const addFiles = (event) => {
    const all = [...composeFiles, ...Array.from(event.target.files || [])];

    event.target.value = "";

    if (all.length > 5 || all.some((file) => file.size > 15 * 1024 * 1024)) {
      setComposeError("Maximum 5 attachments, 15 MB per file.");
      return;
    }

    setComposeError("");
    setComposeFiles(all);
  };

  const removeFile = (index) =>
    setComposeFiles((files) => files.filter((_, i) => i !== index));

  const discardCompose = () => {
    setComposeOpen(false);
    setCompose({ ...emptyCompose });
    setComposeFiles([]);
    setComposeError("");
  };

  const renderComposer = (inline) => {
    const replyLike =
      inline && (composeMode === "reply" || composeMode === "replyAll");

    const ModeIcon =
      composeMode === "replyAll"
        ? ReplyAll
        : composeMode === "forward"
          ? Forward
          : Reply;

    const hasQuote = Boolean(compose.quoteHtml);

    const hasContent =
      compose.body.trim() ||
      /<(img|table)/i.test(compose.html || "") ||
      hasQuote;

    const sizeClass = inline ? "" : composeSize;

    return (
      <>
        {sizeClass === "full" && (
          <div
            className="erp-compose-backdrop"
            onClick={() => setComposeSize("normal")}
          />
        )}
        <form
          className={`erp-compose ${inline ? "inline" : "floating"} ${sizeClass}`}
          onSubmit={sendEmail}
        >
          {/* new message window has a title bar; reply / forward do not (Gmail) */}
          {!inline && (
            <div
              className="erp-compose-head"
              onClick={() => {
                if (composeSize === "min") setComposeSize("normal");
              }}
            >
              <span className="erp-compose-title">{composeTitle}</span>

              <span className="erp-compose-head-btns">
                <button
                  type="button"
                  className="erp-compose-close"
                  title={composeSize === "min" ? "Restore" : "Minimize"}
                  onClick={(event) => {
                    event.stopPropagation();
                    setComposeSize((v) => (v === "min" ? "normal" : "min"));
                  }}
                >
                  <Minus size={18} />
                </button>

                <button
                  type="button"
                  className="erp-compose-close"
                  title={
                    composeSize === "full" ? "Exit full screen" : "Full screen"
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    setComposeSize((v) => (v === "full" ? "normal" : "full"));
                  }}
                >
                  {composeSize === "full" ? (
                    <Minimize2 size={16} />
                  ) : (
                    <Maximize2 size={16} />
                  )}
                </button>

                <button
                  type="button"
                  className="erp-compose-close"
                  title="Close"
                  disabled={sending}
                  onClick={(event) => {
                    event.stopPropagation();
                    closeCompose();
                  }}
                >
                  <X size={18} />
                </button>
              </span>
            </div>
          )}

          {/* SDC-MULTIMAIL: which mailbox this mail is sent from */}
          {activeAccount && (
            <div className="erp-compose-row">
              <label>From</label>
              <span className="erp-compose-from">{activeAccount}</span>
            </div>
          )}

          <div className="erp-compose-row recipients">
            {inline ? (
              <span className="erp-compose-mode" title={composeTitle}>
                <ModeIcon size={17} />
              </span>
            ) : (
              <label htmlFor="erp-compose-to">To</label>
            )}

            {/* chips + ERP project dropdown (picked email -> To, rest of project -> Cc) */}
            <RecipientField
              id="erp-compose-to"
              field="to"
              value={compose.to}
              placeholder="recipient@example.com"
              onChange={setRecipients("to")}
              options={projectOptions}
              loading={projectsLoading}
              onPickProject={pickProjectEmail("to")}
              onMoveEmail={moveRecipient}
              onChipDragStart={showCcForDrag}
            />

            {(!showCc || !showBcc) && (
              <span className="erp-compose-links">
                {!showCc && (
                  <button type="button" onClick={() => setShowCc(true)}>
                    Cc
                  </button>
                )}

                {!showBcc && (
                  <button type="button" onClick={() => setShowBcc(true)}>
                    Bcc
                  </button>
                )}
              </span>
            )}
          </div>

          {showCc && (
            <div className="erp-compose-row recipients">
              <label htmlFor="erp-compose-cc">Cc</label>

              <RecipientField
                id="erp-compose-cc"
                field="cc"
                value={compose.cc}
                placeholder="Optional"
                onChange={setRecipients("cc")}
                options={projectOptions}
                loading={projectsLoading}
                onPickProject={pickProjectEmail("cc")}
                onMoveEmail={moveRecipient}
                onChipDragStart={showCcForDrag}
              />
            </div>
          )}

          {showBcc && (
            <div className="erp-compose-row">
              <label htmlFor="erp-compose-bcc">Bcc</label>
              <input
                id="erp-compose-bcc"
                type="text"
                value={compose.bcc}
                placeholder="Optional - hidden from other recipients"
                onChange={setField("bcc")}
              />
            </div>
          )}

          {/* reply keeps its subject (Re: ...) but does not show it, like Gmail */}
          {!replyLike && (
            <div className="erp-compose-row">
              <label htmlFor="erp-compose-subject">Subject</label>
              <input
                id="erp-compose-subject"
                type="text"
                required
                value={compose.subject}
                onChange={setField("subject")}
              />
            </div>
          )}

          <RichEditor
            key={`${composeMode}-${composeSession}`}
            initialHtml={compose.html || textToHtml(compose.body)}
            focusAtTop={inline}
            minHeight={inline ? 120 : 260}
            onChange={(html, text) => {
              composeDirty.current = true;
              setCompose((value) => ({ ...value, html, body: text }));
            }}
          />

          {/* original mail of a reply: collapsed behind "..." like Gmail */}
          {hasQuote && (
            <>
              <button
                type="button"
                className="erp-compose-ellipsis"
                title={
                  showQuote ? "Hide original message" : "Show original message"
                }
                onClick={() => setShowQuote((value) => !value)}
              >
                •••
              </button>

              {showQuote && (
                <div
                  className="erp-compose-quote"
                  // built only from escaped text (see replyQuote)
                  dangerouslySetInnerHTML={{ __html: compose.quoteHtml }}
                />
              )}
            </>
          )}

          {composeFiles.length > 0 && (
            <div className="erp-compose-files">
              {composeFiles.map((file, index) => (
                <span
                  className="erp-compose-chip"
                  key={`${file.name}-${index}`}
                >
                  <Paperclip size={13} />
                  <span>{file.name}</span>
                  <button
                    type="button"
                    title="Remove"
                    onClick={() => removeFile(index)}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {composeMode === "forward" && detail?.attachments?.length > 0 && (
            <div className="erp-compose-hint">
              Original attachments are not automatically forwarded. Attach them
              manually if needed.
            </div>
          )}

          {composeError && (
            <div className="erp-compose-error" role="alert">
              {composeError}
            </div>
          )}

          <div className="erp-compose-footer">
            <button
              type="submit"
              className="erp-compose-send"
              disabled={
                sending ||
                !compose.to.trim() ||
                !compose.subject.trim() ||
                !hasContent
              }
            >
              <Send size={15} />
              {sending ? "Sending..." : "Send"}
            </button>

            <label
              className="erp-compose-tool"
              title="Attach files (max 5, 15 MB each)"
            >
              <Paperclip size={18} />
              <input type="file" multiple hidden onChange={addFiles} />
            </label>

            <button
              type="button"
              className="erp-compose-tool"
              title="Save draft"
              disabled={sending}
              onClick={saveDraft}
            >
              <Save size={18} />
            </button>

            <span className="erp-compose-spacer" />

            <button
              type="button"
              className="erp-compose-tool"
              title="Discard"
              disabled={sending}
              onClick={discardCompose}
            >
              <Trash2 size={18} />
            </button>
          </div>
        </form>
      </>
    );
  };

  // ====================================================
  // SETTINGS + SIGNATURE
  // ====================================================

  useEffect(() => {
    const controller = new AbortController();

    apiRequest("/signature", { signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted) return;

        const next = {
          html: result.signature?.html || "",
          enabled: result.signature?.enabled !== false,
        };

        setSignature(next);
        setSigDraft(next);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Signature:", err.message);
        }
      });

    return () => controller.abort();
  }, []);

  const openSettings = () => {
    // always start from the saved signature
    setSigDraft(signature);
    setSigMessage(null);
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
    setSigDraft(signature);
    setSigMessage(null);
  };

  const saveSignature = async () => {
    if (sigSaving) return;

    setSigSaving(true);
    setSigMessage(null);

    try {
      const result = await apiRequest("/signature", {
        method: "PUT",
        body: { html: sigDraft.html, enabled: sigDraft.enabled },
      });

      const next = {
        html: result.signature?.html || "",
        enabled: result.signature?.enabled !== false,
      };

      setSignature(next);
      setSigDraft(next);
      setSigMessage({ ok: true, text: "Signature saved." });
    } catch (err) {
      setSigMessage({
        ok: false,
        text: err.message || "Unable to save signature.",
      });
    } finally {
      setSigSaving(false);
    }
  };

  // ====================================================
  // SWITCH / ADD / REMOVE MAIL ACCOUNT (SDC-MULTIMAIL)
  // ====================================================

  const switchAccount = (email) => {
    const next = email && email !== mailboxEmail ? email : "";

    if (next === activeAccount) return;

    // an open message window would save its draft into the wrong account
    if (composeOpen) {
      setError("Close the message window before switching mail account.");
      return;
    }

    // used by every API call from now on
    currentAccount = next;

    // cached mails belong to the old account
    detailCache.current.clear();
    detailInflight.current.clear();
    attachmentCache.current.clear();
    threadCache.current = null;

    setActiveAccount(next);
    setFolders([]);
    setEmails([]);
    setSelectedId(null);
    setCheckedIds([]);
    setDetail(null);
    setRelated([]);
    setSearch("");
    setError("");
    setNotice("");

    navigate("/email/inbox");
    refreshFolder();
  };

  const addAccount = async ({ email, password, label }) => {
    const result = await apiRequest("/accounts", {
      method: "POST",
      body: { email, password, label },
    });

    setAccounts((previous) => [...previous, result.account]);

    return result.account;
  };

  const removeAccount = async (account) => {
    try {
      await apiRequest(`/accounts/${encodeURIComponent(account.id)}`, {
        method: "DELETE",
      });

      setAccounts((previous) =>
        previous.filter((item) => item.id !== account.id),
      );

      if (activeAccount === account.email) switchAccount("");

      setNotice("Mail account removed.");
    } catch (err) {
      setError(err.message || "Unable to remove mail account.");
    }
  };

  // ====================================================
  // UI
  // ====================================================

  return (
    <div className="erp-email">
      <header className="erp-email-header">
        <div className="erp-email-heading">
          <Mail size={28} color="#315cc9" />

          <h2>Email</h2>

          <button
            className="erp-email-compose-btn"
            type="button"
            onClick={() => openCompose("compose")}
          >
            <PenLine size={16} />
            Compose
          </button>
        </div>

        <div className="erp-email-header-actions">
          <div className="erp-email-search">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search in emails..."
            />

            <Search size={18} />
          </div>

          <button
            className="erp-email-icon-btn"
            type="button"
            title="Refresh mailbox"
            disabled={loading || busy}
            onClick={refreshFolder}
          >
            <RefreshCw size={18} />
          </button>

          <button
            className="erp-email-icon-btn"
            type="button"
            title="Settings"
            onClick={() => (showSettings ? closeSettings() : openSettings())}
          >
            <Settings size={18} />
          </button>
        </div>
      </header>

      {notice && (
        <div
          className="erp-email-settings"
          role="status"
          style={{ color: "#18794e" }}
        >
          {notice}
        </div>
      )}

      {error && (
        <div
          className="erp-email-settings"
          role="alert"
          style={{ color: "#b42318" }}
        >
          {error}
        </div>
      )}

      {showSettings && (
        <div
          className="erp-settings-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeSettings();
          }}
        >
          <div
            className="erp-settings-modal"
            role="dialog"
            aria-label="Email settings"
          >
            <div className="erp-settings-head">
              <strong>Email settings</strong>

              <button
                type="button"
                className="erp-compose-close"
                title="Close"
                onClick={closeSettings}
              >
                <X size={18} />
              </button>
            </div>

            <div className="erp-settings-body">
              <section className="erp-settings-section">
                <h4>MailEnable mailbox</h4>

                <div>
                  Logged in as: {activeAccount || mailboxEmail || "Loading..."}
                </div>

                <div className="erp-settings-muted">
                  Inbox, Sent, Drafts, Trash, Spam, Archive and custom folders
                  use IMAP. Sending uses the existing authenticated SMTP API.
                </div>
              </section>

              <section className="erp-settings-section">
                <h4>Signature</h4>

                <label className="erp-settings-check">
                  <input
                    type="checkbox"
                    checked={sigDraft.enabled}
                    onChange={(event) =>
                      setSigDraft((value) => ({
                        ...value,
                        enabled: event.target.checked,
                      }))
                    }
                  />
                  Add this signature to new messages, replies and forwards
                </label>

                <div className="erp-settings-editor">
                  <RichEditor
                    initialHtml={signature.html}
                    minHeight={140}
                    maxImageKB={50}
                    imageStoreWidth={320}
                    imageDefaultWidth={150}
                    onChange={(html) =>
                      setSigDraft((value) => ({ ...value, html }))
                    }
                  />
                </div>

                {sigMessage && (
                  <div
                    className={`erp-settings-msg ${
                      sigMessage.ok ? "ok" : "err"
                    }`}
                    role="status"
                  >
                    {sigMessage.text}
                  </div>
                )}

                <div className="erp-settings-actions">
                  <button
                    type="button"
                    className="erp-compose-send"
                    disabled={sigSaving}
                    onClick={saveSignature}
                  >
                    <Save size={15} />
                    {sigSaving ? "Saving..." : "Save signature"}
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      <div className={`erp-email-layout ${selectedEmail ? "has-detail" : ""}`}>
        {/* SIDEBAR */}
        <aside className="erp-email-folders">
          {/* SDC-MULTIMAIL: main mailbox + extra accounts */}
          <MailAccounts
            primaryEmail={mailboxEmail}
            accounts={accounts}
            active={activeAccount}
            busy={busy}
            onSwitch={switchAccount}
            onAdd={addAccount}
            onRemove={removeAccount}
          />

          {SYSTEM_FOLDERS.map((folder) => {
            const Icon = folder.icon;

            const count =
              folder.id === "inbox"
                ? unreadCount ||
                  folderCount(folders.find((item) => item.id === "inbox"))
                : folderCount(folders.find((item) => item.id === folder.id));

            return (
              <button
                key={folder.id}
                type="button"
                className={`erp-email-folder ${
                  activeFolder === folder.id ? "active" : ""
                }`}
                onClick={() => openFolder(folder.id)}
              >
                <Icon size={20} color={folder.color} fill={folder.fill} />

                <span>{folder.name}</span>

                {count > 0 && <span className="erp-email-count">{count}</span>}
              </button>
            );
          })}

          <div className="erp-email-folder-heading">
            <strong>Folders</strong>

            <button
              type="button"
              title="Create folder"
              disabled={busy}
              onClick={createFolder}
              style={{
                border: 0,
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <Plus size={17} />
            </button>
          </div>

          {customFolders.length === 0 && (
            <div className="erp-email-custom-folder">
              <Folder size={19} color="#9ca3af" />
              <span>No custom folders</span>
            </div>
          )}

          {customFolders.map((folder, i) => {
            const folderColor = FOLDER_COLORS[i % FOLDER_COLORS.length];
            const count = folderCount(folder);

            return (
              <div
                key={folder.path || folder.id}
                className="erp-email-custom-folder"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <button
                  type="button"
                  onClick={() => openFolder(folder.id || folder.path)}
                  style={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    gap: 12,
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                    minWidth: 0,
                    padding: 0,
                    color: "inherit",
                    fontSize: "inherit",
                    fontWeight:
                      activeFolder === (folder.id || folder.path) ? 700 : 500,
                  }}
                >
                  <Folder size={20} color={folderColor} fill={folderColor} />

                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {folder.name}
                  </span>
                </button>

                {count > 0 && <span className="erp-email-count">{count}</span>}

                <button
                  type="button"
                  title="Rename folder"
                  disabled={busy}
                  onClick={() => renameFolder(folder)}
                >
                  <Pencil size={14} />
                </button>

                <button
                  type="button"
                  title="Delete folder"
                  disabled={busy}
                  onClick={() => removeFolder(folder)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </aside>

        {/* MESSAGE LIST */}
        <section className="erp-email-list-panel">
          <div className="erp-email-toolbar">
            <input
              type="checkbox"
              checked={
                filteredEmails.length > 0 &&
                filteredEmails.every((email) => checkedIds.includes(email.key))
              }
              onChange={(event) =>
                setCheckedIds(
                  event.target.checked
                    ? filteredEmails.map((email) => email.key)
                    : [],
                )
              }
            />

            <span>All</span>
            <ChevronDown size={15} />

            <div className="erp-email-toolbar-divider" />

            <button
              type="button"
              title={
                activeFolder === "trash"
                  ? "Permanently delete"
                  : "Move to Trash"
              }
              disabled={busy || !selectedMessages.length}
              onClick={deleteSelected}
            >
              <Trash2 size={17} />
            </button>

            {activeFolder === "trash" ? (
              <button
                type="button"
                title="Restore to Inbox"
                disabled={busy || !selectedMessages.length}
                onClick={restoreSelected}
              >
                <RotateCcw size={17} />
              </button>
            ) : (
              <button
                type="button"
                title="Archive selected"
                disabled={busy || !selectedMessages.length}
                onClick={archiveSelected}
              >
                <Archive size={17} />
              </button>
            )}

            <button
              type="button"
              title="Move to folder"
              disabled={busy || !selectedMessages.length}
              onClick={moveSelected}
            >
              <FolderInput size={17} />
            </button>

            <button
              type="button"
              title="Mark selected email read or unread"
              disabled={busy || !selectedEmail}
              onClick={() => selectedEmail && toggleRead(selectedEmail)}
            >
              <Check size={17} />
            </button>
          </div>

          <div className="erp-email-message-list">
            {loading && (
              <div className="erp-email-empty">
                Loading MailEnable emails...
              </div>
            )}

            {!loading &&
              filteredEmails.map((email) => (
                <div
                  key={email.key}
                  className={`erp-email-message ${
                    selectedEmail?.key === email.key ? "selected" : ""
                  } ${email.unread ? "unread" : ""}`}
                  onClick={() => setSelectedId(email.key)}
                  onMouseEnter={() => prefetchDetail(email)}
                  onMouseLeave={cancelPrefetch}
                >
                  <input
                    type="checkbox"
                    checked={checkedIds.includes(email.key)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleChecked(email.key)}
                  />

                  <div
                    className="erp-email-avatar"
                    style={{
                      backgroundColor: email.color,
                    }}
                  >
                    {email.initials}
                  </div>

                  <div className="erp-email-message-content">
                    <div className="erp-email-message-top">
                      <strong>{email.sender}</strong>
                      <small>{email.date}</small>
                    </div>

                    <div className="erp-email-message-subject">
                      {email.subject}
                    </div>

                    <div className="erp-email-message-bottom">
                      <span>{email.preview}</span>

                      {email.hasAttachments && <Paperclip size={15} />}

                      <button
                        className="erp-email-star-btn"
                        type="button"
                        disabled={busy}
                        title={email.starred ? "Remove star" : "Star email"}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleStar(email);
                        }}
                      >
                        <Star
                          size={18}
                          fill={email.starred ? "#ffbb00" : "none"}
                          color={email.starred ? "#ffbb00" : "#6b7280"}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

            {!loading && !error && filteredEmails.length === 0 && (
              <div className="erp-email-empty">
                {search.trim()
                  ? "No matching emails found."
                  : "No emails found in this folder."}
              </div>
            )}
          </div>
        </section>

        {/* MESSAGE DETAILS */}
        {selectedEmail && (
          <section className="erp-email-detail-panel">
            {selectedEmail ? (
              <>
                <div className="erp-email-detail-toolbar">
                  <div className="erp-email-detail-title">
                    <button
                      type="button"
                      title="Back to list"
                      onClick={() => setSelectedId(null)}
                    >
                      <ArrowLeft size={18} />
                    </button>

                    <h3>{selectedEmail.subject}</h3>
                  </div>

                  <div>
                    {selectedEmail.folder === "drafts" ? (
                      <button
                        type="button"
                        title="Open draft"
                        disabled={!detail || detailLoading}
                        onClick={() => openCompose("draft")}
                      >
                        <Pencil size={18} />
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          title="Reply"
                          disabled={!detail || detailLoading}
                          onClick={() => openCompose("reply")}
                        >
                          <Reply size={18} />
                        </button>

                        <button
                          type="button"
                          title="Reply All"
                          disabled={!detail || detailLoading}
                          onClick={() => openCompose("replyAll")}
                        >
                          <ReplyAll size={18} />
                        </button>

                        <button
                          type="button"
                          title="Forward"
                          disabled={!detail || detailLoading}
                          onClick={() => openCompose("forward")}
                        >
                          <Forward size={18} />
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      title={
                        selectedEmail.folder === "trash"
                          ? "Permanently delete"
                          : "Move to Trash"
                      }
                      disabled={busy}
                      onClick={deleteSelected}
                    >
                      <Trash2 size={18} />
                    </button>

                    <button
                      type="button"
                      title={
                        selectedEmail.starred ? "Remove star" : "Star email"
                      }
                      disabled={busy}
                      onClick={() => toggleStar(selectedEmail)}
                    >
                      <Star
                        size={18}
                        fill={selectedEmail.starred ? "#ffbb00" : "none"}
                        color={
                          selectedEmail.starred ? "#ffbb00" : "currentColor"
                        }
                      />
                    </button>
                  </div>
                </div>

                <div className="erp-email-detail-scroll">
                  <div className="erp-email-sender-row">
                    <div
                      className="erp-email-avatar large"
                      style={{
                        backgroundColor: selectedEmail.color,
                      }}
                    >
                      {selectedEmail.initials}
                    </div>

                    <div className="erp-email-sender-info">
                      <strong>
                        {fromName} <span>{fromAddr}</span>
                      </strong>

                      {detail?.to && <small>To: {asText(detail.to)}</small>}

                      {detail?.cc && <small>CC: {asText(detail.cc)}</small>}
                    </div>

                    <small>
                      {formatDate(detail?.date) || selectedEmail.date}
                    </small>
                  </div>

                  {detailLoading ? (
                    <div
                      className="erp-email-body erp-skeleton"
                      aria-busy="true"
                    >
                      {selectedEmail.preview && (
                        <p className="erp-skeleton-preview">
                          {selectedEmail.preview}
                        </p>
                      )}

                      <span
                        className="erp-skeleton-line"
                        style={{ width: "92%" }}
                      />
                      <span
                        className="erp-skeleton-line"
                        style={{ width: "78%" }}
                      />
                      <span
                        className="erp-skeleton-line"
                        style={{ width: "86%" }}
                      />
                      <span
                        className="erp-skeleton-line"
                        style={{ width: "46%" }}
                      />
                    </div>
                  ) : detailError ? (
                    <div
                      className="erp-email-body"
                      role="alert"
                      style={{ color: "#b42318" }}
                    >
                      {detailError}
                    </div>
                  ) : (
                    <MailThread body={detail?.body} />
                  )}

                  {/* ATTACHMENTS: click card = open, download icon = download */}
                  {!detailLoading &&
                    !detailError &&
                    detail?.attachments?.length > 0 && (
                      <div className="erp-email-attachments">
                        <h4>{detail.attachments.length} Attachments</h4>

                        <div className="erp-email-attachment-grid">
                          {detail.attachments.map((attachment, index) => {
                            const kind = fileKind(
                              attachment.filename,
                              attachment.contentType,
                            );

                            const open = () =>
                              openAttachment(attachment, false);

                            const isBusy = attachmentBusy.startsWith(
                              `${selectedEmail.key}-${attachment.index}-`,
                            );

                            return (
                              <div
                                key={`${attachment.index ?? index}-${attachment.filename || ""}`}
                                className={`erp-email-attachment ${
                                  isBusy ? "busy" : ""
                                }`}
                                role="button"
                                tabIndex={0}
                                title="Click to open"
                                onClick={open}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                  ) {
                                    event.preventDefault();
                                    open();
                                  }
                                }}
                              >
                                <button
                                  type="button"
                                  className="erp-email-attachment-download"
                                  title="Download"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openAttachment(attachment, true);
                                  }}
                                >
                                  <Download size={16} />
                                </button>

                                <span className={`erp-file-badge ${kind.cls}`}>
                                  {kind.label}
                                </span>

                                <strong>
                                  {attachment.filename ||
                                    `Attachment ${index + 1}`}
                                </strong>

                                <small>
                                  {isBusy
                                    ? "Opening..."
                                    : formatSize(attachment.size)}
                                </small>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* earlier mails of the conversation, one card each */}
                  {!detailLoading &&
                    !detailError &&
                    (related.length > 0 ? (
                      <MailHistory
                        items={related}
                        fetchDetail={fetchDetail}
                        resetKey={selectedEmail.key}
                        onOpenAttachment={openAttachment}
                        attachmentBusy={attachmentBusy}
                      />
                    ) : (
                      <MailThread body={detail?.body} older />
                    ))}

                  {/* REPLY / REPLY ALL / FORWARD - Gmail style, right below the mail */}
                  {isInlineCompose
                    ? renderComposer(true)
                    : selectedEmail.folder !== "drafts" && (
                        <div className="erp-email-reply-actions">
                          <button
                            type="button"
                            disabled={!detail || detailLoading}
                            onClick={() => openCompose("reply")}
                          >
                            <Reply size={18} /> Reply
                          </button>

                          <button
                            type="button"
                            disabled={!detail || detailLoading}
                            onClick={() => openCompose("replyAll")}
                          >
                            <ReplyAll size={18} /> Reply All
                          </button>

                          <button
                            type="button"
                            disabled={!detail || detailLoading}
                            onClick={() => openCompose("forward")}
                          >
                            <Forward size={18} /> Forward
                          </button>
                        </div>
                      )}
                </div>
              </>
            ) : (
              <div className="erp-email-no-selection">
                <Mail size={42} />
                <p>Select an email to read</p>
              </div>
            )}
          </section>
        )}
      </div>

      {/* NEW MESSAGE / DRAFT - floating window at bottom-right like Gmail */}
      {composeOpen && !isInlineCompose && renderComposer(false)}
    </div>
  );
}
